import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  User,
  UserAuthorityOverrideRequest,
  UserCreateRequest,
  UserUpdateRequest,
} from '@cortex/domain';

import { cortexApi } from '@/services/cortexApi';
import { resolveApiError } from '@/services/errorMessages';
import { useToast } from '@/components/ui/Toast';
import { useAuth } from '@/auth';
import { AdminMasterDetail } from './AdminMasterDetail';
import { UserList } from './UserList';
import { UserDetail, type EntityOption } from './UserDetail';
import { UserRolesPanel } from './UserRolesPanel';
import { AuthorityOverridePanel } from './AuthorityOverridePanel';

/**
 * Controller for the Users tab of the Admin Module.
 *
 * Server state (the user list) lives in TanStack Query under
 * `['admin','users']`. Mutations invalidate that key on success so the
 * list re-fetches and reflects the change only after the write confirms.
 *
 * Scope follows the caller's permissions:
 *   - `admin.users.global` (AdminSuperUser) sees every company, can place
 *     a new user in any company (company picker fetches /admin/entities).
 *   - otherwise (`admin.users.own_entity`, e.g. BrokerAdmin) the screen
 *     hides the company column and locks new users to the caller's own
 *     company. The API enforces the same narrowing server-side; this is
 *     purely for a cleaner UI.
 */

const USERS_KEY = ['admin', 'users'] as const;
const ENTITIES_KEY = ['admin', 'entities'] as const;
const userDetailKey = (id: string | null) => ['admin', 'users', id, 'detail'] as const;

type DetailMode = 'idle' | 'create' | 'edit';

export function UsersTab() {
  const { showToast } = useToast();
  const { principal, hasPermission } = useAuth();
  const queryClient = useQueryClient();

  const canManageAll = hasPermission('admin.users.global');
  const canManageRoles = canManageAll || hasPermission('admin.users.own_entity');
  const canManageAuthority = canManageAll;
  const callerEntity = principal?.user.entity ?? null;

  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [mode, setMode] = useState<DetailMode>('idle');
  const [formError, setFormError] = useState<string | null>(null);
  const [mobileShowDetail, setMobileShowDetail] = useState(false);

  const usersQuery = useQuery({
    queryKey: USERS_KEY,
    queryFn: () => cortexApi.users.list(),
  });

  // Only global admins need the company picker — and only they are
  // allowed to read /admin/entities.
  const entitiesQuery = useQuery({
    queryKey: ENTITIES_KEY,
    queryFn: () => cortexApi.entities.list(),
    enabled: canManageAll,
  });

  const users = useMemo<readonly User[]>(() => usersQuery.data?.users ?? [], [usersQuery.data]);

  const userDetailQuery = useQuery({
    queryKey: userDetailKey(selectedUserId),
    queryFn: () => cortexApi.users.get(selectedUserId!),
    enabled: selectedUserId !== null && mode === 'edit',
  });

  const entityOptions = useMemo<readonly EntityOption[]>(
    () => (entitiesQuery.data?.entities ?? []).map((e) => ({ id: e.id, name: e.name })),
    [entitiesQuery.data],
  );

  const lockedEntity = useMemo<EntityOption | null>(
    () => (canManageAll || !callerEntity ? null : { id: callerEntity.id, name: callerEntity.name }),
    [canManageAll, callerEntity],
  );

  const listError = usersQuery.isError ? formatError(resolveApiError(usersQuery.error)) : null;

  // Use the detail response when available (has authority); fall back to list row.
  const selectedUser = useMemo<User | null>(
    () => userDetailQuery.data?.user ?? users.find((u) => u.id === selectedUserId) ?? null,
    [userDetailQuery.data, users, selectedUserId],
  );

  const selectedUserAuthority = userDetailQuery.data?.user.authority ?? null;

  const createMutation = useMutation({
    mutationFn: (input: UserCreateRequest) => cortexApi.users.create(input),
    onSuccess: async (response) => {
      setFormError(null);
      showToast(`Invited ${response.user.displayName}.`, 'success');
      await queryClient.invalidateQueries({ queryKey: USERS_KEY });
      setSelectedUserId(response.user.id);
      setMode('edit');
    },
    onError: (err) => {
      const resolved = resolveApiError(err);
      setFormError(formatError(resolved));
      showToast(formatError(resolved), resolved.tone);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: UserUpdateRequest }) =>
      cortexApi.users.update(id, input),
    onSuccess: async (response) => {
      setFormError(null);
      showToast(`Saved ${response.user.displayName}.`, 'success');
      await queryClient.invalidateQueries({ queryKey: USERS_KEY });
      setSelectedUserId(response.user.id);
    },
    onError: (err) => {
      const resolved = resolveApiError(err);
      setFormError(formatError(resolved));
      showToast(formatError(resolved), resolved.tone);
    },
  });

  const setAuthorityMutation = useMutation({
    mutationFn: ({ id, req }: { id: string; req: UserAuthorityOverrideRequest }) =>
      cortexApi.users.setAuthority(id, req),
    onSuccess: async () => {
      showToast('Authority override saved.', 'success');
      if (selectedUserId) {
        await queryClient.invalidateQueries({ queryKey: userDetailKey(selectedUserId) });
      }
    },
    onError: (err) => {
      const resolved = resolveApiError(err);
      showToast(formatError(resolved), resolved.tone);
    },
  });

  const clearAuthorityMutation = useMutation({
    mutationFn: (id: string) => cortexApi.users.clearAuthority(id),
    onSuccess: async () => {
      showToast('Authority override cleared.', 'success');
      if (selectedUserId) {
        await queryClient.invalidateQueries({ queryKey: userDetailKey(selectedUserId) });
      }
    },
    onError: (err) => {
      const resolved = resolveApiError(err);
      showToast(formatError(resolved), resolved.tone);
    },
  });

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const isUpdatingAuthority = setAuthorityMutation.isPending || clearAuthorityMutation.isPending;

  const handleSelect = useCallback((id: string): void => {
    setSelectedUserId(id);
    setMode('edit');
    setFormError(null);
    setMobileShowDetail(true);
  }, []);

  const handleCreateNew = useCallback((): void => {
    setSelectedUserId(null);
    setMode('create');
    setFormError(null);
    setMobileShowDetail(true);
  }, []);

  const handleCancel = useCallback((): void => {
    setMode('idle');
    setSelectedUserId(null);
    setFormError(null);
    setMobileShowDetail(false);
  }, []);

  const handleMobileBack = useCallback((): void => setMobileShowDetail(false), []);

  const handleCreate = useCallback(
    async (input: UserCreateRequest): Promise<void> => {
      setFormError(null);
      await createMutation.mutateAsync(input).catch(() => undefined);
    },
    [createMutation],
  );

  const handleUpdate = useCallback(
    async (id: string, input: UserUpdateRequest): Promise<void> => {
      setFormError(null);
      await updateMutation.mutateAsync({ id, input }).catch(() => undefined);
    },
    [updateMutation],
  );

  const handleSetAuthority = useCallback(
    async (req: UserAuthorityOverrideRequest): Promise<void> => {
      if (!selectedUserId) return;
      await setAuthorityMutation.mutateAsync({ id: selectedUserId, req }).catch(() => undefined);
    },
    [setAuthorityMutation, selectedUserId],
  );

  const handleClearAuthority = useCallback(async (): Promise<void> => {
    if (!selectedUserId) return;
    await clearAuthorityMutation.mutateAsync(selectedUserId).catch(() => undefined);
  }, [clearAuthorityMutation, selectedUserId]);

  return (
    <AdminMasterDetail
      title="User Management"
      subtitle="People who can sign in to CORTEX. Login is handled by Cloudflare Access."
      mobileShowDetail={mobileShowDetail}
      onMobileBack={handleMobileBack}
      list={
        <UserList
          users={users}
          selectedUserId={selectedUserId}
          isLoading={usersQuery.isLoading}
          errorMessage={listError}
          showEntityColumn={canManageAll}
          onSelect={handleSelect}
          onCreateNew={handleCreateNew}
        />
      }
      detail={
        <UserDetail
          mode={mode}
          user={selectedUser}
          entityOptions={entityOptions}
          lockedEntity={lockedEntity}
          isSaving={isSaving}
          errorMessage={formError}
          rolesSlot={
            mode === 'edit' && selectedUser ? (
              <UserRolesPanel
                userId={selectedUser.id}
                entityTypeSlug={selectedUser.entityTypeSlug}
                canManage={canManageRoles}
              />
            ) : null
          }
          authoritySlot={
            mode === 'edit' && selectedUserAuthority ? (
              <AuthorityOverridePanel
                authority={selectedUserAuthority}
                canManage={canManageAuthority}
                isSaving={isUpdatingAuthority}
                onSetAuthority={handleSetAuthority}
                onClearAuthority={handleClearAuthority}
              />
            ) : null
          }
          onCreate={handleCreate}
          onUpdate={handleUpdate}
          onCancel={handleCancel}
        />
      }
    />
  );
}

const formatError = (resolved: { readonly title: string; readonly message: string }): string =>
  `${resolved.title}: ${resolved.message}`;
